import { apiClient } from '@/lib/api';
import type { QuoteOutcome, QuoteLostReason } from '../utils/quote-outcome';
import type {
  TradeQuoteOptions,
  TariffStatus,
  QuoteRequest,
  QuoteResult,
  ProformaExtraction,
  TarifarioImportResult,
  CargaDelHistorial,
  FotoDeTarifa,
  SaveQuoteRequest,
  SavedQuote,
  TariffSearchResponse,
  TariffChapter,
  QuoteOutcomeResult,
  QuoteFunnel,
  RequoteResult,
  CustomsSimulationResponse,
  EditableRate,
  RatePatch,
} from '../types/trade.types';

const BASE = '/api/trade';

export const tradeService = {
  /** Catálogos para los selects del cotizador, en una sola llamada. */
  getQuoteOptions: async (): Promise<TradeQuoteOptions> => {
    return apiClient.get(`${BASE}/options`);
  },

  /** Cotiza el embarque y, si se informa la partida y el FOB, liquida tributos. */
  quote: async (request: QuoteRequest): Promise<QuoteResult> => {
    return apiClient.post(`${BASE}/quote`, request);
  },

  /** Estado de vigencia del tarifario, para el aviso de tarifas vencidas. */
  getTariffStatus: async (): Promise<TariffStatus> => {
    return apiClient.get(`${BASE}/tariff/status`);
  },

  /**
   * Guarda la opción elegida como cotización.
   *
   * Solo se manda el id de la opción, nunca los importes: el servidor
   * recalcula la cotización para que nadie pueda guardar un precio inventado.
   */
  saveQuote: async (request: SaveQuoteRequest): Promise<SavedQuote> => {
    return apiClient.post(`${BASE}/quote/save`, request);
  },

  /**
   * Lee la proforma del proveedor y devuelve las líneas propuestas.
   *
   * No escribe nada: la tabla que vuelve es para revisar y confirmar.
   */
  extractProforma: async (file: File): Promise<ProformaExtraction> => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post(`${BASE}/proforma/extract`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Un PDF de varias páginas puede tardar: el default es muy corto.
      timeout: 180_000,
    });
  },

  // ── Aduana ──

  /** Busca partidas por código o descripción. `q` de al menos 2 caracteres. */
  searchTariffCodes: async (q: string): Promise<TariffSearchResponse> => {
    return apiClient.get(`${BASE}/aduana/partidas`, { params: { q } });
  },

  /** Los capítulos del arancel, para navegar sin escribir. */
  getTariffChapters: async (): Promise<{ chapters: TariffChapter[] }> => {
    return apiClient.get(`${BASE}/aduana/capitulos`);
  },

  /** Liquida los tributos con las tasas de la partida real. */
  simulateCustoms: async (input: {
    tariffCode: string;
    fobValue: number;
    freightValue: number;
    insuranceValue?: number;
    insuranceRate?: number;
    via?: 'SEA' | 'AIR' | 'LAND';
    quantity?: number;
    exchangeRate?: number;
    /** Moneda de FOB, flete y seguro. La conversión la hace el servidor. */
    currency?: 'USD' | 'BOB';
  }): Promise<CustomsSimulationResponse> => {
    return apiClient.post(`${BASE}/aduana/simular`, input);
  },

  // ── Tarifas ──

  /** Las tarifas del tenant para la tabla del Tarifario. */
  listRates: async (params?: {
    legType?: string;
    q?: string;
    /** Apila debajo las tarifas que reemplazaron cargas anteriores del Excel. */
    historicas?: boolean;
  }): Promise<{ rates: EditableRate[]; conteo: { vigentes: number; historicas: number } }> => {
    return apiClient.get(`${BASE}/tarifario/tarifas`, { params });
  },

  /**
   * Corrige una tarifa.
   *
   * Solo monto, vigencia, días y nota. Naviera, origen, destino y equipo no se
   * editan: cambiarlos no es corregir esta tarifa, es otra tarifa distinta.
   */
  updateRate: async (
    id: string,
    patch: RatePatch
  ): Promise<{ rate: EditableRate; breakdownOutdated: boolean }> => {
    return apiClient.patch(`${BASE}/tarifario/tarifas/${id}`, patch);
  },

  /** La URL del PDF de la cotización. Se abre en una pestaña nueva. */
  quotePdfUrl: (orderId: string): string => `${BASE}/quote/${orderId}/pdf`,

  /** Registra qué contestó el cliente. El servidor valida la transición. */
  setQuoteOutcome: async (
    id: string,
    body: { outcome: QuoteOutcome; lostReason?: QuoteLostReason | null; lostNotes?: string | null }
  ): Promise<QuoteOutcomeResult> => {
    return apiClient.patch(`${BASE}/quote/${id}/estado`, body);
  },

  /**
   * Recalcula la misma ruta con los precios de hoy.
   *
   * **No guarda nada.** Devuelve las opciones actuales y la comparación contra
   * lo cotizado; guardar es un paso aparte, porque el precio viejo puede estar
   * ya prometido al cliente.
   */
  requote: async (id: string): Promise<RequoteResult> => {
    return apiClient.get(`${BASE}/quote/${id}/recotizar`);
  },

  /** El embudo: cuántas se mandaron, cuántas se ganaron, por qué se perdieron. */
  quoteFunnel: async (): Promise<QuoteFunnel> => {
    return apiClient.get(`${BASE}/quotes/embudo`);
  },

  /**
   * Lee el tarifario de Delicia.
   *
   * `confirm: false` (por defecto) solo devuelve el reporte sin escribir nada:
   * importar cambia el precio de todas las cotizaciones nuevas, así que primero
   * se ve qué entra y qué se rechaza.
   */
  importTarifario: async (file: File, confirm = false): Promise<TarifarioImportResult> => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post(`${BASE}/tarifario/import?dryRun=${confirm ? 'false' : 'true'}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // 400 filas con upserts: puede tardar.
      timeout: 300_000,
    });
  },

  /**
   * Trae el tipo de cambio oficial del BCB ahora, sin esperar al worker.
   *
   * Devuelve `rate: null` solo si el BCB respondió sin un valor usable; si no
   * responde nada, el servidor contesta 503 con el motivo y esto lanza.
   */
  actualizarTipoDeCambio: async (): Promise<{
    rate: number | null;
    fecha: string | null;
    fuente: string | null;
    empresasActualizadas: number;
  }> => {
    return apiClient.post(`${BASE}/tipo-cambio/actualizar`);
  },

  /** Las cargas del tarifario, de la más reciente a la más vieja. */
  historialTarifario: async (): Promise<CargaDelHistorial[]> => {
    return apiClient.get(`${BASE}/tarifario/historial`);
  },

  /** Las tarifas que reemplazó una carga, congeladas como estaban. */
  detalleCargaTarifario: async (
    id: string
  ): Promise<{ carga: CargaDelHistorial; fotos: FotoDeTarifa[] }> => {
    return apiClient.get(`${BASE}/tarifario/historial/${id}`);
  },
};
